import { LitElement, html, nothing } from 'lit';
import { t, currentLang } from '../i18n';
import { formatFileSize } from '../helpers';
import { sanitizeImageUrl } from '../url-sanitizer';
import type { UploadEntry } from '../types';
import type { UploadFileActionDetail } from './shared';
import { defineZenComponent } from './register';

export class ZenUploadFileList extends LitElement {
  static properties = {
    entries: { attribute: false },
  };

  declare entries: UploadEntry[];

  constructor() {
    super();
    this.entries = [];
  }

  createRenderRoot() {
    return this;
  }

  private emit(detail: UploadFileActionDetail) {
    this.dispatchEvent(new CustomEvent<UploadFileActionDetail>('upload-file-action', {
      bubbles: true,
      composed: true,
      detail,
    }));
  }

  render() {
    if (this.entries.length === 0) return nothing;
    return html`${this.entries.map((entry) => {
      const statusClass = entry.status === 'analyzing' ? 'analyzing' : entry.status === 'done' ? 'analyzed' : '';
      const fileName = entry.file.name || (currentLang === 'en' ? 'Clipboard image' : 'クリップボード画像');
      const previewUrl = sanitizeImageUrl(entry.dataUrl);
      return html`
        <div class="upload-file-row ${statusClass}" id=${`row_${entry.id}`}>
          ${previewUrl
            ? html`<img src=${previewUrl} alt="">`
            : html`<div style="width:48px;height:48px;background:var(--bg);border-radius:6px;"></div>`}
          <div class="upload-file-info">
            <div class="upload-file-name">${fileName}</div>
            <div class="upload-file-size">${formatFileSize(entry.file.size)}</div>
            ${entry.summary ? html`<div class="upload-file-summary">${entry.summary}</div>` : nothing}
            ${entry.goals && entry.goals.length > 0 ? html`
              <div class="upload-goal-links">
                ${entry.goals.map((goal) => html`
                  <button class="upload-goal-link" @click=${() => this.emit({ action: 'navigate-goal', goalId: goal.id })}>
                    <span class="mi mi-xs">flag</span> ${goal.title}
                  </button>
                `)}
              </div>
            ` : nothing}
          </div>
          <div class="upload-file-actions">
            ${entry.status === 'pending' || entry.status === 'analyzing'
              ? html`
                <span class="spinner spinner-sm"></span>
                <span style="font-size:12px;color:var(--text-muted);">${t('analyzing')}</span>
                <button
                  class="btn btn-sm btn-outline"
                  style="margin-left:4px;font-size:11px;padding:3px 8px;"
                  @click=${() => this.emit({ action: 'cancel-analysis', uploadId: entry.id })}
                >${t('cancel')}</button>
              `
              : html`
                <span style="font-size:12px;color:var(--accent);font-weight:500;"><span class="mi mi-xs">check</span> ${t('done')}</span>
                <button class="btn btn-sm btn-danger" @click=${() => this.emit({ action: 'remove-file', uploadId: entry.id })}>
                  <span class="mi mi-xs">close</span>
                </button>
              `}
          </div>
        </div>
      `;
    })}`;
  }
}

defineZenComponent('zen-upload-file-list', ZenUploadFileList);
